import { HeroImage } from "@/components/auth/hero-image";
import * as Sentry from "@sentry/react";
import { OnboardingPage } from "@/components/auth/onboarding-page";
import { useAppForm } from "@/helpers/app-form";
import { queryClient } from "@/helpers/tanstack-client";
import {
  getCurrentOrganization,
  getGetCurrentOrganizationQueryKey,
  getGetTerritoriesQueryKey,
  getTerritories,
  useGetCurrentOrganization,
  useGetTerritories,
  useSubmitOnboarding,
} from "@/models/backend";
import { Breadcrumbs, CircularProgress, Grid, MenuItem, Stack, Typography } from "@mui/material";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import z from "zod";
import { Link, LinkButton } from "@/components/core/tanstack-link";

export const Route = createFileRoute("/_auth/onboarding/about-you")({
  loader: async () => {
    await queryClient.ensureQueryData({
      queryFn: getCurrentOrganization,
      queryKey: getGetCurrentOrganizationQueryKey(),
    });
    await queryClient.ensureQueryData({
      queryFn: () => getTerritories({ language: "en" }),
      queryKey: getGetTerritoriesQueryKey(),
    });
  },
  component: RouteComponent,
});

const aboutYouSchema = z.object({
  role: z.string(),
  use_case: z.string(),
  country: z.string(),
  source: z.string(),
});

function RouteComponent() {
  const { data: organization } = useGetCurrentOrganization();
  const { data: terriories } = useGetTerritories();
  const { mutate: submitOnboarding, isPending: isSubmitting } = useSubmitOnboarding();
  const navigate = useNavigate();

  const onSubmit = ({ value }: { value: z.input<typeof aboutYouSchema> }) => {
    const data = aboutYouSchema.parse(value);
    submitOnboarding(
      {
        data: Object.entries(data).map(([key, val]) => ({
          question: key,
          answer: val,
        })),
      },
      {
        onSettled: () => {
          navigate({ to: "/onboarding/invoices" });
        },
        onError: (e) => {
          // Don’t use snackbar, the user shouldn’t know.
          Sentry.captureException(e);
        },
      },
    );
  };

  const form = useAppForm({
    validators: { onSubmit: aboutYouSchema },
    defaultValues: {
      role: "",
      use_case: "",
      country: organization?.locale.split("_")[1] ?? "",
      source: "",
    },
    onSubmit,
  });

  return (
    <OnboardingPage
      page={
        <form.AppForm>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <Stack spacing={6} sx={{ maxWidth: "600px" }}>
              <Breadcrumbs separator=">">
                <Typography>About you</Typography>
                <Link to="/onboarding/invoices">Invoices</Link>
                <Link to="/onboarding/email">Email</Link>
              </Breadcrumbs>
              <Stack spacing={1}>
                <Typography variant="h3">Tell us about you</Typography>
                <Typography variant="body1" color="text.secondary">
                  This helps us highlight the features you need
                </Typography>
              </Stack>
              <Grid container columnSpacing={4} rowSpacing={6} columns={{ xs: 6, md: 12 }}>
                <Grid size={6}>
                  <form.AppField name="role">
                    {(field) => (
                      <field.TextField label="Role" select fullWidth>
                        <MenuItem value="Owner/Managing Director">Owner/Managing Director</MenuItem>
                        <MenuItem value="CFO/FD/Head of Finance">CFO/FD/Head of Finance</MenuItem>
                        <MenuItem value="Accountant/Bookkeeper">Accountant/Bookkeeper</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </field.TextField>
                    )}
                  </form.AppField>
                </Grid>
                <Grid size={6}>
                  <form.AppField name="use_case">
                    {(field) => (
                      <field.TextField label="What do you need Trove for?" select fullWidth>
                        <MenuItem value="Chasing down unpaid invoices">
                          Chasing down unpaid invoices
                        </MenuItem>
                        <MenuItem value="Coordinating chasing across a team">
                          Coordinating chasing across a team
                        </MenuItem>
                        <MenuItem value="Both">Both</MenuItem>
                      </field.TextField>
                    )}
                  </form.AppField>
                </Grid>
                <Grid size={6}>
                  <form.AppField name="country">
                    {(field) => (
                      <field.TextField
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        label="Where are you?"
                        select
                        fullWidth
                      >
                        {terriories?.map((territory) => (
                          <MenuItem key={territory.id} value={territory.id}>
                            {territory.label}
                          </MenuItem>
                        ))}
                      </field.TextField>
                    )}
                  </form.AppField>
                </Grid>
                <Grid size={6}>
                  <form.AppField name="source">
                    {(field) => (
                      <field.TextField
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        label="How did you hear about us?"
                        select
                        fullWidth
                      >
                        <MenuItem value="Friend or colleague">Friend or colleague</MenuItem>
                        <MenuItem value="Google">Google</MenuItem>
                        <MenuItem value="AI Search">AI Search</MenuItem>
                        <MenuItem value="LinkedIn">LinkedIn</MenuItem>
                        <MenuItem value="App marketplace (e.g. Xero, QuickBooks)">
                          App marketplace (e.g. Xero, QuickBooks)
                        </MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </field.TextField>
                    )}
                  </form.AppField>
                </Grid>
                <Grid size={12} sx={{ display: "flex" }}>
                  <LinkButton to="/onboarding/invoices" variant="outlined">
                    Skip
                  </LinkButton>
                  <form.SubmitButton
                    sx={{ ml: "auto" }}
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={24} /> : null}
                  >
                    Continue
                  </form.SubmitButton>
                </Grid>
              </Grid>
            </Stack>
          </form>
        </form.AppForm>
      }
      hero={
        <HeroImage
          copy="Great support there when you need it"
          url="/assets/onboarding-2.svg"
          imageMaxWidth="700px"
          imageMarginTop={6}
        />
      }
    />
  );
}
